import React, { useState, useEffect } from "react";
import { Plus, Search, ChevronLeft, ChevronRight, Calendar, X } from "lucide-react";
import CreditsSummary from "./_creditsummary";
import AddCreditsModal from "./messagecenter/_addcreditsmodal";
import { useGetCommunicationCreditTranactionsQuery } from "../../lib/apiSlice/communication/communicationSlice.tsx";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear } from 'date-fns';

// Skeleton Loader Component for Transactions
const TransactionSkeleton = () => {
  return (
    <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
      <div className="p-4 border-gray-200 border-b">
        <h2 className="font-medium text-lg">Transaction History</h2>
      </div>
      <table className="divide-y divide-gray-200 min-w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Type</th>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Paid By</th>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Amount</th>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Credits</th>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Date</th>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Payment Method</th>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Status</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {[...Array(5)].map((_, i) => (
            <tr key={i}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="bg-gray-200 rounded-full w-24 h-6 animate-pulse"></div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="bg-gray-200 rounded w-16 h-4 animate-pulse"></div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="bg-gray-200 rounded w-16 h-4 animate-pulse"></div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="bg-gray-200 rounded w-32 h-4 animate-pulse"></div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="bg-gray-200 rounded w-24 h-4 animate-pulse"></div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="bg-gray-200 rounded-full w-20 h-6 animate-pulse"></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DateFilterModal = ({ isOpen, onClose, onApply, months, years }) => {
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  const handleApply = () => {
    onApply(selectedMonth, selectedYear);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-medium text-lg">Select Month and Year</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <p className="block mb-1 font-medium text-gray-700 text-sm">Month</p>
            <select
              className="block py-2 pr-10 pl-3 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full sm:text-sm text-base"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">Select Month</option>
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <p className="block mb-1 font-medium text-gray-700 text-sm">Year</p>
            <select
              className="block py-2 pr-10 pl-3 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full sm:text-sm text-base"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="">Select Year</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="bg-white hover:bg-gray-50 px-4 py-2 border border-gray-300 rounded-md font-medium text-gray-700 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!selectedMonth || !selectedYear}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 shadow-sm px-4 py-2 border border-transparent rounded-md font-medium text-white text-sm disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default function CreditManagement() {
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const { data: MessageCredits, isLoading, isFetching, error } = useGetCommunicationCreditTranactionsQuery();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [customMonth, setCustomMonth] = useState("");
  const [customYear, setCustomYear] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Generate years for dropdown (last 10 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  // Generate months for dropdown
  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    setCurrentPage(1);
    
    if (filter === "custom") {
      setIsDateModalOpen(true);
    }
  };

  const handleApplyDateFilter = (month, year) => {
    setCustomMonth(month);
    setCustomYear(year);
    setDateFilter("custom");
    setCurrentPage(1);
  };

  const filterTransactions = () => {
    if (!MessageCredits?.transactions) return [];

    return MessageCredits.transactions.filter((transaction) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        transaction.paid_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.amount?.toString().includes(searchTerm) ||
        transaction.credits?.toString().includes(searchTerm);

      // Status filter
      const matchesStatus =
        statusFilter === "all" || transaction.status?.toLowerCase() === statusFilter.toLowerCase();

      // Date filter
      let matchesDate = true;
      const transactionDate = new Date(transaction.date_paid);
      const today = new Date();

      switch (dateFilter) {
        case "today":
          matchesDate = format(transactionDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
          break;
        case "yesterday":
          matchesDate = format(transactionDate, 'yyyy-MM-dd') === format(subDays(today, 1), 'yyyy-MM-dd');
          break;
        case "week":
          matchesDate = transactionDate >= startOfWeek(today) && transactionDate <= endOfWeek(today);
          break;
        case "month":
          matchesDate = transactionDate >= startOfMonth(today) && transactionDate <= endOfMonth(today);
          break;
        case "lastMonth":
          matchesDate = transactionDate >= startOfMonth(subMonths(today, 1)) && 
                       transactionDate <= endOfMonth(subMonths(today, 1));
          break;
        case "year":
          matchesDate = transactionDate >= startOfYear(today) && transactionDate <= endOfYear(today);
          break;
        case "custom":
          if (customMonth && customYear) {
            const monthStart = new Date(`${customYear}-${customMonth}-01`);
            const monthEnd = endOfMonth(monthStart);
            matchesDate = transactionDate >= monthStart && transactionDate <= monthEnd;
          }
          break;
        default:
          matchesDate = true;
      }

      return matchesSearch && matchesStatus && matchesDate;
    });
  };

  const filteredTransactions = filterTransactions();
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="font-semibold text-gray-800 text-2xl">Credit Management</h1>
        <button
          onClick={() => setIsTopUpOpen(true)}
          className="flex items-center bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-md text-white"
        >
          <Plus className="mr-2 w-4 h-4" /> Top Up Credits
        </button>
      </div>

      {/* Credit Summary */}
      <CreditsSummary />

      {/* Filters */}
      <div className="bg-white shadow-sm p-4 border border-gray-200 rounded-lg">
        <div className="gap-4 grid grid-cols-1 md:grid-cols-3">
          {/* Search */}
          <div className="relative">
            <div className="left-0 absolute inset-y-0 flex items-center pl-3 pointer-events-none">
              <Search className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block bg-white py-2 pr-3 pl-10 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full sm:text-sm leading-5 placeholder-gray-500"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Status Filter */}
          <select
            className="block py-2 pr-10 pl-3 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full sm:text-sm text-base"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All Statuses</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>

          {/* Date Filter */}
          <div className="relative">
            <div className="left-0 absolute inset-y-0 flex items-center pl-3 pointer-events-none">
              <Calendar className="w-5 h-5 text-gray-400" />
            </div>
            <select
              className="block py-2 pr-10 pl-10 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full sm:text-sm text-base"
              value={dateFilter}
              onChange={(e) => handleDateFilterChange(e.target.value)}
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="year">This Year</option>
              <option value="custom">Select Month/Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Date Filter Modal */}
      <DateFilterModal
        isOpen={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        onApply={handleApplyDateFilter}
        months={months}
        years={years}
      />

      {/* Transaction History */}
      {(isLoading || isFetching) ? (
        <TransactionSkeleton />
      ) : (
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex justify-between items-center p-4 border-gray-200 border-b">
            <h2 className="font-medium text-lg">Transaction History</h2>
            <div className="text-gray-500 text-sm">
              Showing {filteredTransactions.length} transactions
            </div>
          </div>
          <table className="divide-y divide-gray-200 min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Paid By</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Credits</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Payment Method</th>
                <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedTransactions.length > 0 ? (
                paginatedTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center bg-blue-50 px-2.5 py-0.5 border border-blue-200 rounded-full font-medium text-blue-700 text-xs">
                        Purchase
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">{transaction.paid_by}</td>
                    <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">KES. {transaction.amount}</td>
                    <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">{transaction.credits}</td>
                    <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                      {new Date(transaction.date_paid).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">MPESA</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-medium text-xs ${
                        transaction.status.toLowerCase() === "success"
                          ? "bg-green-100 text-green-800"
                          : transaction.status.toLowerCase() === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : transaction.status.toLowerCase() === "failed"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                      }`}>
                        {transaction.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-gray-500 text-sm text-center">
                    No transactions found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {filteredTransactions.length > itemsPerPage && (
            <div className="flex justify-between items-center px-4 sm:px-6 py-3 border-gray-200 border-t">
              <div className="sm:hidden flex flex-1 justify-between">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="inline-flex relative items-center bg-white hover:bg-gray-50 disabled:opacity-50 px-4 py-2 border border-gray-300 rounded-md font-medium text-gray-700 text-sm"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="inline-flex relative items-center bg-white hover:bg-gray-50 disabled:opacity-50 ml-3 px-4 py-2 border border-gray-300 rounded-md font-medium text-gray-700 text-sm"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:justify-between sm:items-center">
                <div>
                  <p className="text-gray-700 text-sm">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredTransactions.length)}</span> of{' '}
                    <span className="font-medium">{filteredTransactions.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="inline-flex z-0 relative -space-x-px shadow-sm rounded-md" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="inline-flex relative items-center bg-white hover:bg-gray-50 disabled:opacity-50 px-2 py-2 border border-gray-300 rounded-l-md font-medium text-gray-500 text-sm"
                    >
                      <span className="sr-only">First</span>
                      <ChevronLeft className="w-5 h-5" />
                      <ChevronLeft className="-ml-2 w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="inline-flex relative items-center bg-white hover:bg-gray-50 disabled:opacity-50 px-2 py-2 border border-gray-300 font-medium text-gray-500 text-sm"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center px-4">
                      <span className="text-gray-700 text-sm">
                        Page {currentPage} of {totalPages}
                      </span>
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="inline-flex relative items-center bg-white hover:bg-gray-50 disabled:opacity-50 px-2 py-2 border border-gray-300 font-medium text-gray-500 text-sm"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="inline-flex relative items-center bg-white hover:bg-gray-50 disabled:opacity-50 px-2 py-2 border border-gray-300 rounded-r-md font-medium text-gray-500 text-sm"
                    >
                      <span className="sr-only">Last</span>
                      <ChevronRight className="w-5 h-5" />
                      <ChevronRight className="-ml-2 w-5 h-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Top Up Dialog */}
      {isTopUpOpen && <AddCreditsModal onClose={() => setIsTopUpOpen(false)} />}
    </div>
  );
}